import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatCard, Section, IconButton, BottomSheet } from '../../shared/components';
import { useCurrentUser, useSignOut } from '../../shared/auth/AuthProvider';
import { getOperationTimesRepository } from '../../shared/services/operationTimes/repositoryFactory';
import { formatDuration } from '../../shared/utils/datetime';
import { TimerIcon, ReportIcon, AuditIcon, LogoutIcon, PaletteIcon } from '../../shared/icons';
import { ThemeSwitcher } from '../../shared/theme/ThemeSwitcher';

const operationTimesRepo = getOperationTimesRepository();

/**
 * Home / Dashboard de LCC. Tarjetas presionables que llevan al modulo correspondiente.
 * "Tiempo Operativo" se calcula en vivo con datos reales de Control de Tiempos.
 * "Reportes" y "Auditoría" son accesos directos a esos modulos (ya activos).
 */
export function DashboardPage() {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const signOut = useSignOut();
  const [todaySeconds, setTodaySeconds] = useState(0);
  const [themeSheetOpen, setThemeSheetOpen] = useState(false);

  useEffect(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    operationTimesRepo
      .list({ userId: user.id, dateFrom: todayStart.toISOString(), page: 1, pageSize: 200 })
      .then((res) => {
        const total = res.items.reduce((sum, r) => sum + r.durationSeconds, 0);
        setTodaySeconds(total);
      });
  }, [user.id]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Buenos días';
    if (h < 19) return 'Buenas tardes';
    return 'Buenas noches';
  }, []);

  return (
    <div>
      <div className="safe-top flex items-start justify-between px-4 pb-2 pt-5">
        <div>
          <p className="text-sm text-ink-400">{greeting},</p>
          <h1 className="text-2xl font-bold text-ink-900">{user.fullName.split(' ')[0]}</h1>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <IconButton
            icon={<PaletteIcon width={20} height={20} />}
            label="Tema visual"
            onClick={() => setThemeSheetOpen(true)}
            className="h-11 w-11 text-ink-400 hover:bg-ink-100 hover:text-brand-600"
          />
          {signOut && (
            <IconButton
              icon={<LogoutIcon width={20} height={20} />}
              label="Cerrar sesión"
              onClick={signOut}
              className="h-11 w-11 text-ink-400 hover:bg-ink-100 hover:text-critical"
            />
          )}
        </div>
      </div>

      <BottomSheet open={themeSheetOpen} onClose={() => setThemeSheetOpen(false)} title="Tema visual">
        <div className="pb-2 pt-1">
          <ThemeSwitcher />
        </div>
      </BottomSheet>

      <div className="p-4">
        <Section title="Resumen de hoy">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            <StatCard icon={<TimerIcon />} label="Tiempo Operativo" value={formatDuration(todaySeconds)} tone="brand" onClick={() => navigate('/tiempos')} />
            <StatCard icon={<ReportIcon />} label="Indicadores y KPIs" value="Reportes" tone="info" onClick={() => navigate('/reportes')} />
            <StatCard icon={<AuditIcon />} label="Historial de cambios" value="Auditoría" tone="warning" onClick={() => navigate('/auditoria')} />
          </div>
        </Section>
      </div>
    </div>
  );
}
